import React, { FC } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { BrandThemeProvider } from '@rover/kibble/themes';
import getMatchedRoute from '@rover/react-app/src/server/renderPage/getMatchedRoute';
import { reducer as locationInput } from '@rover/react-lib/src/components/formFields/LocationInput/LocationInput.duck';
import { Provider as ReduxProvider } from '@rover/react-lib/src/components/utils/Redux';
import { A11yContextProvider } from '@rover/react-lib/src/context/A11yContext';
import LocationContext from '@rover/react-lib/src/context/LocationContext';
import PageContext from '@rover/react-lib/src/context/PageContext';
import useHistoryContext from '@rover/react-lib/src/context/useHistoryContext';
import useQueryContext from '@rover/react-lib/src/context/useQueryContext';
import { reducer as searchBox } from '@rover/react-lib/src/pages/index/HomePage/components/SearchBox/SearchBox.duck';
import { reducer as homePage } from '@rover/react-lib/src/pages/index/HomePage/HomePage.duck';
import { reducer as internalLinking } from '@rover/react-lib/src/pages/landing/InternalLinking/internalLinking.duck';
import { reducer as serviceLanding } from '@rover/react-lib/src/pages/landing/ServiceLanding/serviceLanding.duck';
import { reducer as editPetInfo } from '@rover/react-lib/src/pages/pets/EditPetProfilePage/EditPetProfilePage.duck';
import { reducer as searchFilters } from '@rover/react-lib/src/pages/search/SearchPage/SearchFilters.duck';
import { reducer as searchMap } from '@rover/react-lib/src/pages/search/SearchPage/SearchMap.duck';
import { reducer as searchModal } from '@rover/react-lib/src/pages/search/SearchPage/SearchModal.duck';
import { reducer as searchPage } from '@rover/react-lib/src/pages/search/SearchPage/SearchPage.duck';
import { reducer as systemInfo } from '@rover/react-lib/src/pages/systems/SystemInfoPage/SystemInfoPage.duck';
import { reducer as breeds } from '@rover/react-lib/src/redux/ducks/breeds.duck';
import { reducer as citiesAndNeighborhoods } from '@rover/react-lib/src/redux/ducks/citiesAndNeighborhoods.duck';
import { reducer as countryConfiguration } from '@rover/react-lib/src/redux/ducks/countryConfiguration.duck';
import { reducer as countryFooterLinks } from '@rover/react-lib/src/redux/ducks/countryFooterLinks.duck';
import { reducer as countryRegionLinks } from '@rover/react-lib/src/redux/ducks/countryRegionLinks.duck';
import { reducer as currentUser } from '@rover/react-lib/src/redux/ducks/currentUser.duck';
import { reducer as experiments } from '@rover/react-lib/src/redux/ducks/experiments.duck';
import { reducer as favoriteSitters } from '@rover/react-lib/src/redux/ducks/favoriteSitters.duck';
import { reducer as footer } from '@rover/react-lib/src/redux/ducks/footer.duck';
import { reducer as frontendConfiguration } from '@rover/react-lib/src/redux/ducks/frontendConfiguration.duck';
import { reducer as frontendEvents } from '@rover/react-lib/src/redux/ducks/frontendEvents.duck';
import { reducer as geolocation } from '@rover/react-lib/src/redux/ducks/geolocation.duck';
import { reducer as globalAnnouncements } from '@rover/react-lib/src/redux/ducks/globalAnnouncements.duck';
import { reducer as hrefLangs } from '@rover/react-lib/src/redux/ducks/hrefLangs.duck';
import { reducer as marketingOptIn } from '@rover/react-lib/src/redux/ducks/marketingOptIn.duck';
import { reducer as newsletterSignup } from '@rover/react-lib/src/redux/ducks/newsletterSignup.duck';
import { reducer as notificationsCenter } from '@rover/react-lib/src/redux/ducks/notificationsCenter.duck';
import { reducer as pets } from '@rover/react-lib/src/redux/ducks/pets.duck';
import { reducer as search } from '@rover/react-lib/src/redux/ducks/search.duck';
import { reducer as serviceLinks } from '@rover/react-lib/src/redux/ducks/serviceLinks.duck';
import { reducer as serviceTypes } from '@rover/react-lib/src/redux/ducks/serviceTypes.duck';
import { useObservabilityMetadataConceptInfo } from '@rover/rsdk/src/index.common';
import { RoverStatsigProvider } from '@rover/rsdk/src/modules/Statsig';
import RoverBrandProvider from '@rover/shared/js/providers/RoverBrandContextProvider';
import getPath from '@rover/shared/js/utils/getPath';
import getRoverBrand from '@rover/utilities/getRoverBrand';

export type Props = RouteComponentProps & {
  children: React.ReactNode;
};

export const reducers = {
  // All reducers for the app go here
  breeds,
  citiesAndNeighborhoods,
  countryConfiguration,
  countryFooterLinks,
  countryRegionLinks,
  currentUser,
  editPetInfo,
  experiments,
  favoriteSitters,
  footer,
  frontendConfiguration,
  frontendEvents,
  geolocation,
  globalAnnouncements,
  homePage,
  hrefLangs,
  internalLinking,
  locationInput,
  marketingOptIn,
  newsletterSignup,
  notificationsCenter,
  pets,
  search,
  searchBox,
  searchFilters,
  searchMap,
  searchModal,
  searchPage,
  serviceLinks,
  serviceTypes,
  serviceLanding,
  systemInfo,
};

const BaseProvider: FC<Props> = ({ location, children }) => {
  const path = getPath();
  const { pageRoute } = getMatchedRoute(path);
  const brand = getRoverBrand();
  if (!pageRoute) throw new Error('No route found');

  const { route } = pageRoute;
  const history = useHistoryContext();
  const query = useQueryContext();

  // this is usually handled by the RSDKProvider,
  // but we need to do it earlier to get this metadata on all metrics
  const { pageName, concept, componentName } = route || {};
  useObservabilityMetadataConceptInfo({
    concept,
    uiComponentName: componentName,
    uiName: pageName,
  });

  return (
    <A11yContextProvider>
      <ReduxProvider reducers={reducers} query={query} history={history}>
        <RoverBrandProvider brand={brand}>
          <BrandThemeProvider brand={brand}>
            <RoverStatsigProvider>
              <PageContext.Provider value={route}>
                <LocationContext.Provider value={location}>{children}</LocationContext.Provider>
              </PageContext.Provider>
            </RoverStatsigProvider>
          </BrandThemeProvider>
        </RoverBrandProvider>
      </ReduxProvider>
    </A11yContextProvider>
  );
};

export default withRouter(BaseProvider);
